
import Navbar from "@/components/Navbar";
import TokenDisplay from "@/components/TokenDisplay";
import GibRating from "@/components/GibRating";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import ImageHoverPopup from "@/components/ui/image-hover-popup";
import { Badge } from "@/components/ui/badge";
import { Bitcoin, Blocks, Cpu, Globe, Building2, Bot, Rocket, Brain, Crop } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const ShaanProfile = () => {
  const user = {
    name: "Shaan Ray",
    role: "iSPAI Head of Web3",
    solutionBounties: 6,
    domains: [
      "Blockchain",
      "Web3",
      "Decentralization", 
      "Cryptocurrency",
      "Smart Contracts",
      "Tokenomics",
      "DeFi",
      "NFTs",
      "DAO Governance"
    ],
    industries: [
      { name: "Blockchain", icon: Blocks },
      { name: "Cryptocurrency", icon: Bitcoin },
      { name: "Technology", icon: Cpu },
      { name: "Global", icon: Globe },
      { name: "Enterprise", icon: Building2 },
      { name: "AI", icon: Brain }
    ],
    confirmedSolutions: 28,
    confirmedKnowledgeBases: 55,
    tokenBalance: 180,
    bio: "Shaan Ray is a Blockchain Expert at iSPAI, bringing extensive knowledge in decentralized technologies and their applications. With a background in computer science and cryptography, Shaan has been deeply involved in the blockchain space since its early days. He specializes in smart contract development, decentralized finance (DeFi), non-fungible tokens (NFTs), and decentralized autonomous organizations (DAOs). Shaan is passionate about the potential of blockchain to revolutionize industries and create more transparent and equitable systems. He has worked on various blockchain projects, from developing secure and scalable smart contracts to advising companies on blockchain adoption strategies. As a Blockchain Expert at iSPAI, Shaan will play a key role in exploring and implementing blockchain-based solutions to enhance knowledge sharing, collaboration, and innovation within the platform."
  };

  return (
    <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6 col-span-2">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start space-x-6">
                <div className="flex flex-col items-center gap-4">
                  <ImageHoverPopup 
                    imageSrc="/lovable-uploads/01817a77-50a5-4764-a5be-a08ff84c2c0d.png"
                    imageAlt="Shaan Ray"
                  >
                    <Avatar className="h-36 w-36 cursor-pointer">
                      <AvatarImage 
                        src="/lovable-uploads/01817a77-50a5-4764-a5be-a08ff84c2c0d.png" 
                        alt="Shaan Ray"
                      />
                      <AvatarFallback>SR</AvatarFallback>
                    </Avatar>
                  </ImageHoverPopup>
                  <div className="flex gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="bg-white text-black hover:bg-gray-100"
                        >
                          BIO
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-96">
                        <div className="space-y-2">
                          <h4 className="text-lg font-semibold">Shaan Ray</h4>
                          <p className="text-sm text-gray-600">{user.bio}</p>
                        </div>
                      </PopoverContent>
                    </Popover>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="bg-white text-black hover:bg-gray-100"
                    >
                      NW
                    </Button>
                  </div>
                  <div className="w-full">
                    <h3 className="font-serif text-xl font-semibold text-black mb-3">Industries</h3>
                    <div className="grid grid-cols-1 gap-2">
                      {user.industries.map((industry) => {
                        const Icon = industry.icon;
                        return (
                          <div 
                            key={industry.name}
                            className="flex items-center space-x-2 p-3 border-2 border-black rounded-lg"
                          >
                            <Icon className="h-5 w-5 text-black" />
                            <span className="text-sm font-medium text-black">{industry.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h2 className="font-serif text-3xl font-bold text-black">{user.name}</h2>
                    <a href="/ispai-channel" className="ml-2">
                      <img 
                        src="/lovable-uploads/d7afdb11-1c93-4596-9b11-91cb70981610.png" 
                        alt="iSPAI Channel" 
                        className="w-16 h-16 hover:scale-110 transition-transform"
                      />
                    </a>
                  </div>
                  <p className="text-lg font-medium text-black mb-4">{user.role}</p>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Solution Bounties</h3>
                        <span className="text-4xl font-bold mb-2">{user.solutionBounties}</span>
                        <Link 
                          to="/solution-bounties/shaan"
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          View Details →
                        </Link>
                      </div>
                    </Card>
                    <GibRating rating={9.1} />
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Confirmed Solutions</h3>
                        <span className="text-4xl font-bold">{user.confirmedSolutions}</span>
                      </div>
                    </Card>
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Knowledge Bases</h3>
                        <span className="text-4xl font-bold">{user.confirmedKnowledgeBases}</span>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
          </Card>
          
          <div className="space-y-6">
            <TokenDisplay balance={user.tokenBalance} />
            <Card className="p-6">
              <h3 className="font-serif text-xl font-semibold text-black mb-3">Domains</h3>
              <div className="flex flex-wrap gap-2">
                {user.domains.map((domain) => (
                  <Badge 
                    key={domain} 
                    variant="outline"
                    className="border-2 border-black text-black"
                  >
                    {domain}
                  </Badge>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ShaanProfile;
